import { Route as RootRoute } from "@/routes/__root";
import { mfCentralRefetchInterval } from "creso-core/constants";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  useMfCentralImportState,
  useMfCentralStatus,
  usePortfolioSummaryV2,
} from "creso-core/hooks";
import { Client } from "creso-core/types";
import { useMemo } from "react";
import {
  importEmptyImg,
  NoDataAnimation,
  SyncingLottieAnimation,
  TimeoutLottieAnimation,
} from "web-core";
import { Button, cn } from "web-design-system";

export function EmptyExternalPortfolio({ client }: { client: Client }) {
  const navigate = useNavigate();

  const { modal } = useSearch({ from: RootRoute.id });

  const { data: importStatus } = useMfCentralStatus(
    client.investment_account ?? "",
    true,
    mfCentralRefetchInterval
  );

  const importState = useMfCentralImportState(importStatus);

  const { data: summary } = usePortfolioSummaryV2(
    client?.investment_account ?? "",
    importStatus?.status === "SUCCESS"
  );

  const externalHoldings = summary?.find((s) => s.source_type === "EXTERNAL");

  const isEmptyHoldings =
    importState === "idle" &&
    importStatus?.last_fetch_at &&
    externalHoldings &&
    Number(externalHoldings?.current_value) === 0;

  const isImportDialogOpen = modal === "import-holdings";

  const uiState = useMemo(() => {
    const importHoldings = (
      <Button
        onClick={() =>
          navigate({
            to: ".",
            search: (prev) => ({
              ...prev,
              client_id: client.id,
              modal: "import-holdings" as const,
            }),
          })
        }
        className={cn("mt-12")}
        size="lg"
        variant="outline"
      >
        {isEmptyHoldings ? "Import Holdings" : "Open MF Central"}
      </Button>
    );

    if (importState === "in-progress") {
      return {
        title: "Importing Holdings...",
        description:
          "External portfolio should appear on the platform in a few mins.",
        action: null,
        graphic: !isImportDialogOpen ? (
          <SyncingLottieAnimation height={240} width={240} />
        ) : (
          <img className="h-full aspect-auto" src={importEmptyImg} />
        ),
      };
    }
    if (importState === "expired") {
      return {
        title: "Taking longer than usual",
        description:
          "Holdings are still being imported, might take up to 10-15 mins, come back later.",
        action: null,
        graphic: <TimeoutLottieAnimation height={240} width={240} />,
      };
    }
    if (isEmptyHoldings) {
      return {
        title: "No Holdings Found",
        description: "No mutual fund holdings found.",
        action: importHoldings,
        graphic: <NoDataAnimation loop={false} height={240} width={240} />,
      };
    }
    return {
      title: "Import Holdings",
      description: "Find opportunities and track external holdings.",
      action: importHoldings,
      graphic: <img className="h-full aspect-auto" src={importEmptyImg} />,
    };
  }, [importState, client.id, isEmptyHoldings, navigate, isImportDialogOpen]);

  return (
    <div className="flex flex-row flex-1 bg-card border border-border rounded-lg items-center">
      <div className="flex items-center flex-1 justify-center gap-24">
        <div className="flex flex-col items-start w-100 shrink-0">
          <span className="text-xl font-semibold">{uiState.title}</span>
          <span className="mt-2 text-muted-foreground">
            {uiState.description}
          </span>
          {uiState.action}
        </div>
        <div className="flex justify-center items-center h-45 shrink-0">
          {uiState.graphic}
        </div>
      </div>
    </div>
  );
}
